import { oneOf, str, nullableStr, bool } from '../../lib/viewState';
import { INVOICE_KINDS, type Brand, type FinanceReport, type InvoiceStatus, type InvoiceWithNames } from '../../../shared/types';

export const MONTHS_FULL = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

export const PAID_WINDOW = 8;
export const EXPENSE_WINDOW = 40;

export const INVOICE_STATUS_LABELS: Record<InvoiceStatus, string> = {
  draft: 'Draft',
  sent: 'Sent',
  paid: 'Paid',
  overdue: 'Overdue',
  void: 'Void',
};

export function invoiceStatusTone(s: InvoiceStatus): 'accent' | 'gold' | 'danger' | undefined {
  return s === 'paid' ? 'accent' : s === 'sent' ? 'gold' : s === 'overdue' ? 'danger' : undefined;
}

export function effectiveInvoiceBrand(invoice: Pick<InvoiceWithNames, 'brand' | 'project_brand'>): Brand {
  return invoice.project_brand ?? invoice.brand;
}

/** Display status: a sent invoice past its due date reads as overdue. */
export function displayStatus(inv: { status: InvoiceStatus; due_date: string | null }, today: string): InvoiceStatus {
  if (inv.status === 'sent' && inv.due_date && inv.due_date < today) return 'overdue';
  return inv.status;
}

export function matchesQuery(query: string, ...haystacks: Array<string | null | undefined>): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return haystacks.some((h) => h?.toLowerCase().includes(q));
}

/** "#0231" in the input → "231" in storage (Henry stores unpadded). */
export function rawInvoiceNumber(display: string): string {
  return display.trim().replace(/^#/, '').replace(/^0+(?=\d)/, '');
}

export const financeViewCodec = {
  tab: oneOf('tab', ['invoices', 'expenses', 'reports', 'data-quality'], 'invoices'), query: str('q'),
  invKind: oneOf('kind', ['', ...INVOICE_KINDS], ''), invClient: nullableStr('client'), expCategory: nullableStr('category'),
  showAllPaid: bool('allpaid'), showAllExpenses: bool('allexp'),
  unpaid: bool('unpaid'), undated: bool('undated'),
};

export function capitalize(s: string): string {
  return s.slice(0, 1).toUpperCase() + s.slice(1);
}

// ── Reports FY switcher (toolbar-right nested tabs per the mockup) ────────

export function fyList(report: FinanceReport): string[] {
  const keys = new Set(report.quarters.map((q) => q.key.split(' ')[0]));
  return [...keys].sort((a, b) => b.localeCompare(a));
}
